import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ControlledInput from "../CustomInput/ControlledInput";
import { ChromePicker } from "react-color";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { Box, Button, Paper, Popover } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../TranslationWrapper/TranslationWrapper";
import FormField from "./FormField";
import { useOnClickOutsideElement } from "../../Helpers/useOnClickedOutsideElement";

function FormColor(props) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState("");
  const anchorEl = useRef<HTMLElement>();
  const savedColorValue = useWatch({ name: props.name });
  const { field } = useController({ name: props.name, control });

  useEffect(() => {
    if (color !== savedColorValue) {
      setColor(savedColorValue);
    }
  }, [savedColorValue]);

  const paperRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutsideElement(paperRef, () => {
    if (open) {
      if (savedColorValue) {
        setColor(savedColorValue);
      }
      setOpen(false);
    }
  });

  const paper = useMemo(() => {
    return open ? (
      <Box ref={anchorEl} sx={{ position: "relative" }}>
        <Popover anchorEl={() => anchorEl.current} open={true}>
          <Paper
            ref={paperRef}
            style={{
              // position: "absolute",
              // zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 8,
            }}
          >
            <ChromePicker
              color={color ?? ""}
              disableAlpha={props.disableAlpha}
              onChange={(color) => {
                const alpha = Math.round(color.rgb.a * 255);

                setColor(color.hex + alpha.toString(16));
              }}
              styles={{ default: { picker: { boxShadow: "none" } } }}
            />
            <Button
              color={"primary"}
              onClick={() => {
                field.onChange(color ?? "#000");
                setOpen(false);
              }}
              variant={"contained"}
            >
              <TranslationWrapper tDefault={"Aanpassen"} tKey={"global.colerpicker"} />
            </Button>
          </Paper>
        </Popover>
      </Box>
    ) : null;
  }, [open, field]);

  return (
    <>
      {paper}
      <FormField
        key={props.name}
        {...props}
        Field={ControlledInput}
        fieldProps={({ value, onChange }) => ({
          controlled: false,
          nullable: props.nullable ?? undefined,
          InputProps: {
            startAdornment: (
              <Button
                onClick={() => {
                  setOpen(true);
                }}
                style={{
                  ...(value == null || value === ""
                    ? {
                        // transparent checker pattern
                        background: `linear-gradient(45deg, #808080 25%, transparent 25%),
                                 linear-gradient(-45deg, #808080 25%, transparent 25%),
                                 linear-gradient(45deg, transparent 75%, #808080 75%),
                                 linear-gradient(-45deg, transparent 75%, #808080 75%)`,
                        backgroundColor: "#bbb",
                        backgroundSize: "10px 10px",
                        backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0px",
                      }
                    : {
                        backgroundColor: value,
                      }),
                  marginRight: 16,
                  padding: "8px 0",
                }}
                variant={"contained"}
                // disabled={true}
                size={"small"}
              />
            ),
          },
          onHandleValue: (value) => {
            onChange({ target: { value: value ? String(value) : value } }, value ? String(value) : value);
          },
        })}
        rules={{
          validate: (color: string) => {
            if (color && color.length > 0 && !CSS.supports("color", color)) {
              return getTranslation("Input is not valid!");
            }
            return true;
          },
          ...(props.rules ? props.rules : {}),
        }}
      />
    </>
  );
}

FormColor.propTypes = {
  name: PropTypes.string.isRequired, // FormField props
  tKey: PropTypes.string, // FormField props
  tDefault: PropTypes.string, // FormField props
  permissionKey: PropTypes.string, // FormField props
  gridSizes: PropTypes.object, // FormField props
  rules: PropTypes.object, // FormField props
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]), // FormField props

  nullable: PropTypes.bool,
  disableAlpha: PropTypes.bool,
};

FormColor.defaultProps = {
  disableAlpha: false,
};

export default FormColor;
